import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import { BackdropLoader, Icon, toast } from '@livable/saas-react-components';
import { Button, Chip, Link, Paper, Typography } from '@mui/material';
import { observer } from 'mobx-react-lite';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import ChangeNumber from 'src/components/settings/ChangeNumber';
import ChangeNumberModal from 'src/components/settings/ChangeNumberModal';
import { TotpSetup } from 'src/components/settings/TotpSetup';
import { USER_SETTINGS_KEYS } from 'src/constants/userSetting';
import { useStore } from 'src/stores';
import { formatPhoneForDisplay } from 'src/utils/phone';
import useStyles from './styles';
const SMS = 'SMS';
const TOTP = 'TOTP';
const TwoFactorAuth = observer(() => {
  var _store$account$userPr, _store$account$userPr2;
  const classes = useStyles();
  const store = useStore();
  const _useTranslation = useTranslation(),
    t = _useTranslation.t;
  const userPhone = (_store$account$userPr = store.account.userProfile.phone) !== null && _store$account$userPr !== void 0 ? _store$account$userPr : '';
  const mfaEnabled = store.account.userProfile.mfaEnabled;
  const methods = Array.from((_store$account$userPr2 = store.account.userProfile.mfaMethods) !== null && _store$account$userPr2 !== void 0 ? _store$account$userPr2 : []);
  const preferredMethod = store.account.userProfile.preferredMfaMethod;

  // A backend build from before this feature reports mfaEnabled with no method list or preference;
  // such an account is on SMS. Every derived value below reads through these two, never the raw list.
  const smsEnabled = methods.length ? methods.includes(SMS) : mfaEnabled;
  const totpEnabled = methods.includes(TOTP);
  const preferred = preferredMethod !== null && preferredMethod !== void 0 ? preferredMethod : totpEnabled ? TOTP : SMS;
  const _useState = useState(store.account.userProfile.phoneConfirmed),
    _useState2 = _slicedToArray(_useState, 2),
    phoneConfirmed = _useState2[0],
    setPhoneConfirmed = _useState2[1];
  const _useState3 = useState(false),
    _useState4 = _slicedToArray(_useState3, 2),
    isLoading = _useState4[0],
    setIsLoading = _useState4[1];
  const _useState5 = useState(false),
    _useState6 = _slicedToArray(_useState5, 2),
    totpSetupIsOpen = _useState6[0],
    setTotpSetupIsOpen = _useState6[1];
  const setMethods = nextMethods => store.account.userProfile.setMfaMethods(nextMethods);
  const methodsWith = function () {
    let method = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : null;
    return [...new Set([...(smsEnabled ? [SMS] : []), ...(totpEnabled ? [TOTP] : []), method])].filter(Boolean);
  };
  const enableSms = async () => {
    const resp = await store.account.enableAccountMfaAsync.run({
      method: SMS
    });
    if (resp && resp.isSuccessful) {
      setMethods(methodsWith(SMS));
      setPhoneConfirmed(true);
      localStorage.setItem(USER_SETTINGS_KEYS.phoneConfirmed, true);
      toast('success', {
        body: [t('mfa.sms_enabled')]
      });
    }
  };
  const disableMethod = async method => {
    const resp = await store.account.disableAccountMfaAsync.run({
      method
    });
    if (resp && resp.isSuccessful) {
      setMethods(methodsWith().filter(item => item !== method));
      toast('success', {
        body: [t(method === TOTP ? 'mfa.totp_disabled' : 'mfa.sms_disabled')]
      });
    }
  };
  const runWithLoader = request => {
    setIsLoading(true);
    request().finally(() => setIsLoading(false));
  };
  const handleToggleSms = () => runWithLoader(() => smsEnabled ? disableMethod(SMS) : enableSms());
  const handleToggleTotp = () => {
    if (!totpEnabled) {
      setTotpSetupIsOpen(true);
      return;
    }
    runWithLoader(() => disableMethod(TOTP));
  };
  const handleTotpEnabled = () => {
    setTotpSetupIsOpen(false);
    setMethods(methodsWith(TOTP));
  };
  const renderStatus = (enabled, isPreferred) => /*#__PURE__*/React.createElement("span", {
    className: classes.flex
  }, /*#__PURE__*/React.createElement(Chip, {
    color: enabled ? 'success' : 'default',
    label: enabled ? t('mfa.status_on') : t('mfa.status_off'),
    size: "small"
  }), enabled && isPreferred && /*#__PURE__*/React.createElement(Typography, {
    className: classes.preferredHint,
    variant: "body3",
    component: "span"
  }, t('mfa.preferred')));
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Paper, {
    variant: "formWrapper"
  }, /*#__PURE__*/React.createElement(BackdropLoader, {
    isOpen: isLoading
  }), /*#__PURE__*/React.createElement(Typography, {
    variant: "h4",
    gutterBottom: true
  }, t('mfa.two_factor_auth')), /*#__PURE__*/React.createElement(Typography, {
    variant: "body3",
    component: "p",
    marginBottom: "18px"
  }, t('mfa.two_factor_auth_hint'), " ", /*#__PURE__*/React.createElement(Link, {
    href: "/"
  }, t('general.learn_more'))), /*#__PURE__*/React.createElement("div", {
    className: "".concat(classes.authFieldWrapper, " ").concat(mfaEnabled ? classes.success : classes.danger)
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.authIcon
  }, /*#__PURE__*/React.createElement(Icon, {
    color: mfaEnabled ? 'success' : 'error',
    fontSize: "large",
    type: mfaEnabled ? 'shield-check' : 'lock-locked'
  })), /*#__PURE__*/React.createElement(Typography, {
    variant: "h5"
  }, mfaEnabled ? t('mfa.two_factor_auth_enabled') : t('mfa.two_factor_auth_disabled'))), phoneConfirmed ? /*#__PURE__*/React.createElement("div", {
    className: classes.verifiedMobile
  }, /*#__PURE__*/React.createElement("span", {
    className: classes.flex
  }, /*#__PURE__*/React.createElement(Icon, {
    type: "phone"
  }), /*#__PURE__*/React.createElement(Typography, {
    variant: "h6"
  }, t('general.verified'), ": ", formatPhoneForDisplay(userPhone))), /*#__PURE__*/React.createElement(ChangeNumberModal, {
    phoneNumber: userPhone,
    setPhoneConfirmed: setPhoneConfirmed
  })) : /*#__PURE__*/React.createElement(ChangeNumber, {
    phoneNumber: userPhone,
    setPhoneConfirmed: setPhoneConfirmed
  }), /*#__PURE__*/React.createElement("div", {
    className: classes.methodBlock
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.methodHeader
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "h5"
  }, t('mfa.method_sms')), renderStatus(smsEnabled, preferred === SMS)), /*#__PURE__*/React.createElement(Typography, {
    variant: "body3",
    component: "p",
    marginBottom: "12px"
  }, t('mfa.method_sms_hint')), /*#__PURE__*/React.createElement(Button, {
    color: smsEnabled ? 'error' : 'primary',
    disabled: !phoneConfirmed && !smsEnabled,
    fullWidth: true,
    onClick: handleToggleSms,
    variant: "contained",
    'custom-attribute': '302-TwoFactorAuth'
  }, smsEnabled ? t('general.disable') : t('general.enable'), " ", t('mfa.method_sms').toLowerCase())), /*#__PURE__*/React.createElement("div", {
    className: classes.methodBlock
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.methodHeader
  }, /*#__PURE__*/React.createElement(Typography, {
    variant: "h5"
  }, t('mfa.method_totp')), renderStatus(totpEnabled, preferred === TOTP)), /*#__PURE__*/React.createElement(Typography, {
    variant: "body3",
    component: "p",
    marginBottom: "12px"
  }, t('mfa.method_totp_hint')), /*#__PURE__*/React.createElement(Button, {
    color: totpEnabled ? 'error' : 'primary',
    disabled: totpSetupIsOpen,
    fullWidth: true,
    onClick: handleToggleTotp,
    variant: "contained",
    'custom-attribute': '305-TwoFactorAuth-totp'
  }, totpEnabled ? t('general.disable') : t('general.enable'), " ", t('mfa.method_totp').toLowerCase()), totpSetupIsOpen && /*#__PURE__*/React.createElement(TotpSetup, {
    associate: () => store.account.associateTotpAsync.run(),
    enable: data => store.account.enableAccountMfaAsync.run(data),
    onCancel: () => setTotpSetupIsOpen(false),
    onEnabled: handleTotpEnabled,
    verify: data => store.account.verifyTotpAsync.run(data)
  }))));
});
export default TwoFactorAuth;